import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useAtom } from 'jotai';
import { currentOrganizationAtom } from '../store/organizationAtoms';
import type { ChartPreferences, TooltipFormatting, ChartDisplayPreferences } from '@shared/schemas/chartPreferences';
import { defaultChartPreferences } from '@shared/schemas/chartPreferences';
import type { User } from '@shared/schemas/user';
import { userService, organizationService } from '../services/firestoreService';
import type { ColorPalette } from '../lib/colorPalettes';

interface ChartPreferencesContextType {
  preferences: ChartPreferences;
  tooltipFormatting: TooltipFormatting;
  displayPreferences: ChartDisplayPreferences;
  activeDefaultPaletteId: string;
  customPalettes: ColorPalette[];
  isLoading: boolean;
  updateUserChartPreferences: (preferences: Partial<ChartPreferences>) => Promise<void>;
}

const ChartPreferencesContext = createContext<ChartPreferencesContextType | undefined>(undefined);

interface ChartPreferencesProviderProps {
  children: React.ReactNode;
}

export const ChartPreferencesProvider: React.FC<ChartPreferencesProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [currentOrganization] = useAtom(currentOrganizationAtom);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [customPalettes, setCustomPalettes] = useState<ColorPalette[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load user profile when currentUser changes
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!currentUser?.uid) {
        setUserProfile(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const profile = await userService.getById(currentUser.uid);
        setUserProfile(profile);
      } catch (error) {
        console.error('Error loading user profile for chart preferences:', error);
        setUserProfile(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserProfile();
  }, [currentUser?.uid]);

  // Combine organization preferences (as defaults) with user preferences (as overrides)
  const preferences = useMemo(() => {
    const orgPrefs = currentOrganization?.chartPreferences || defaultChartPreferences;
    const userPrefs = userProfile?.chartPreferences;

    if (!userPrefs) {
      return orgPrefs;
    }

    // Deep merge: user preferences override organization preferences
    const merged = {
      tooltipFormatting: {
        ...defaultChartPreferences.tooltipFormatting,
        ...(orgPrefs.tooltipFormatting || {}),
        ...(userPrefs.tooltipFormatting || {}),
      },
      displayPreferences: {
        ...defaultChartPreferences.displayPreferences,
        ...(orgPrefs.displayPreferences || {}),
        ...(userPrefs.displayPreferences || {}),
      },
    } as ChartPreferences;

    return merged;
  }, [currentOrganization?.chartPreferences, userProfile?.chartPreferences]);

  // Determine the active default palette ID
  const activeDefaultPaletteId = useMemo(() => {
    // Priority: User's preference -> Org preference -> System default
    const userDefault = userProfile?.chartPreferences?.defaultPaletteId;
    const orgDefault = currentOrganization?.chartPreferences?.defaultPaletteId;
    // console.log('[ChartPrefsContext] User default palette ID:', userDefault);
    // console.log('[ChartPrefsContext] Org default palette ID:', orgDefault);
    const systemDefault = 'fireforge'; // Ensure 'fireforge' is a valid ID from COLOR_PALETTES

    const finalId = userDefault || orgDefault || systemDefault;
    // console.log('[ChartPrefsContext] Final activeDefaultPaletteId:', finalId);
    return finalId;
  }, [userProfile?.chartPreferences?.defaultPaletteId, currentOrganization?.chartPreferences?.defaultPaletteId]);

  const updateUserChartPreferences = async (newPreferences: Partial<ChartPreferences>) => {
    if (!currentUser?.uid) {
      throw new Error('User not authenticated');
    }

    // Get current user preferences or use default structure
    const currentUserPrefs = userProfile?.chartPreferences || {
      tooltipFormatting: {},
      displayPreferences: {},
    };
    
    // Deep merge the new preferences with existing ones
    const updatedPreferences = {
      tooltipFormatting: {
        ...defaultChartPreferences.tooltipFormatting,
        ...(currentUserPrefs.tooltipFormatting || {}),
        ...(newPreferences.tooltipFormatting || {}),
      },
      displayPreferences: {
        ...defaultChartPreferences.displayPreferences,
        ...(currentUserPrefs.displayPreferences || {}),
        ...(newPreferences.displayPreferences || {}),
      },
    } as ChartPreferences;

    await userService.update(currentUser.uid, {
      chartPreferences: updatedPreferences,
    });

    // Update local state to reflect the change immediately
    setUserProfile(prev => prev ? {
      ...prev,
      chartPreferences: updatedPreferences,
    } : null);
  };

  const contextValue: ChartPreferencesContextType = {
    preferences,
    tooltipFormatting: preferences.tooltipFormatting,
    displayPreferences: preferences.displayPreferences,
    activeDefaultPaletteId,
    customPalettes,
    isLoading,
    updateUserChartPreferences,
  };

  return (
    <ChartPreferencesContext.Provider value={contextValue}>
      {children}
    </ChartPreferencesContext.Provider>
  );
};

export const useChartPreferences = () => {
  const context = useContext(ChartPreferencesContext);
  if (context === undefined) {
    throw new Error('useChartPreferences must be used within a ChartPreferencesProvider');
  }
  return context;
};

// Hook to get just tooltip formatting preferences
export const useTooltipFormatting = () => {
  const { tooltipFormatting } = useChartPreferences();
  return tooltipFormatting;
};

// Hook to get just display preferences
export const useChartDisplayPreferences = () => {
  const { displayPreferences } = useChartPreferences();
  return displayPreferences;
};

// Hook to get custom palettes
export const useCustomPalettes = () => {
  const { customPalettes } = useChartPreferences();
  return customPalettes;
};

// ADDED: Hook to get activeDefaultPaletteId
export const useActiveDefaultPaletteId = () => {
  const { activeDefaultPaletteId } = useChartPreferences();
  return activeDefaultPaletteId;
}; 